import { faro, LogLevel as FaroLogLevel } from '@grafana/faro-react';
import { useCallback } from 'react';

import { LogLevel, LogMessage } from '../types';

// Helper function to stringify context values
const stringifyContextValues = (context: Record<string, unknown>): Record<string, string> => {
  const stringifiedContext: Record<string, string> = {};

  for (const [key, value] of Object.entries(context)) {
    stringifiedContext[key] = typeof value === 'string' ? value : JSON.stringify(value);
  }

  return stringifiedContext;
};

interface UseLoggerServiceProps {
  isInitialized: boolean;
  isRemoteLogger: boolean;
  provider: 'remote' | 'console' | 'none';
}

export const useLoggerService = ({ isInitialized, isRemoteLogger, provider }: UseLoggerServiceProps) => {
  const processLog = useCallback(
    (level: LogLevel, message: string, context: Record<string, unknown>) => {
      // Skip all logging if provider is 'none'
      if (provider === 'none') {
        return;
      }

      const logMessage: LogMessage = {
        level,
        message,
        timestamp: Date.now(),
        context
      };

      // Console logging for development environment or console provider
      if (provider === 'console') {
        console[level](message, logMessage.context);
      }

      if (!isInitialized && isRemoteLogger) {
        console.warn('Logger not initialized');
        return;
      }

      // Remote logging
      if (isRemoteLogger && isInitialized) {
        const stringifiedContext = context ? stringifyContextValues(context) : {};
        try {
          if (level === 'error') {
            // For errors, use error tracking
            faro.api.pushError(new Error(message), {
              // Allow duplicate logs to be sent in succession
              skipDedupe: true,
              context: stringifiedContext
            });
            // Still log to console for local debugging visibility
            console.error(message);
          } else {
            // For other log levels, use standard logging
            faro.api.pushLog([message], {
              level: level as FaroLogLevel,
              // Allow duplicate logs to be sent in succession
              skipDedupe: true,
              context: stringifiedContext
            });
          }
        } catch (error) {
          console.error('Error processing log:', error);
          throw error; // Re-throw for retry mechanism
        }
      }
    },
    [isInitialized, isRemoteLogger, provider]
  );

  return { processLog };
};
